import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { isMadeEasyOrg } from '../../helpers/getOrganization';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

// "HH:mm:ss" / "HH:mm" → 12-hour clock (e.g. "2:30 PM"). Parsed manually so it
// works regardless of timezone/date context.
const formatTime12 = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  if (Number.isNaN(hour)) return null;
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${meridiem}`;
};

// Minutes since midnight from "HH:mm:ss" / "HH:mm", or null if unparseable.
const parseTimeMinutes = (value) => {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
};

const MAX_TIMING_SPAN_MINUTES = 12 * 60;

// Batch class timing as "start – end" (12-hour), tolerating a missing end.
// Returns null (meaning: don't show the Batch Timing field at all) when the
// start/end span exceeds 12 hours, since that usually signals bad/placeholder data.
const formatTiming = (batch) => {
  const start = formatTime12(batch?.startTime);
  const end = formatTime12(batch?.endTime);
  if (start && end) {
    const startMinutes = parseTimeMinutes(batch?.startTime);
    const endMinutes = parseTimeMinutes(batch?.endTime);
    if (
      startMinutes != null
      && endMinutes != null
      && Math.abs(startMinutes - endMinutes) > MAX_TIMING_SPAN_MINUTES
    ) {
      return null;
    }
    return `${start} – ${end}`;
  }
  return start || end || '—';
};

// The batch id behind a selectedBatches entry, tolerating both shapes the server
// stores: a plain id ({ batchId: 'uuid' }) and a nested object ({ batchId: { id } }).
function getSelectedBatchId(batch) {
  const raw = batch?.batchId;
  return (raw && typeof raw === 'object' ? raw.id : raw) || null;
}

// An entry the order is still waiting on: marked primary with no batch chosen. The
// server hydrates `name` only for entries that HAVE a batch, so these rows carry no
// batch fields at all and would otherwise be filtered out of the list entirely.
function isBatchPending(batch) {
  return Boolean(batch?.isPrimary) && !getSelectedBatchId(batch);
}

// Values a venue field can carry that say "there is no venue" rather than naming one:
// an online batch has nowhere to be, and placeholder text is not an address either.
const VENUE_PLACEHOLDERS = new Set([
  '-', '—', 'na', 'n/a', 'nil', 'none', 'null', 'tbd', 'online', 'live online', 'live-online',
]);

// Whether this batch has a venue worth a column. Blank and online batches would only
// ever render "—", so the field is dropped for them instead of shown empty.
function hasVenue(batch) {
  const venue = String(batch?.venueName || '').trim().toLowerCase();
  return venue !== '' && !VENUE_PLACEHOLDERS.has(venue);
}

// Whether any course this order tracks a batch for is still without one — what makes
// "choose your preferred batch after purchase" true rather than noise. Only entries
// that exist in selectedBatches count: a product with no batch concept at all (test
// papers, online-only) carries no entries, and so has nothing to pick later.
export function hasUnselectedBatch(orderItems) {
  return (orderItems || []).some((item) => {
    const batches = item?.productMeta?.selectedBatches;
    return Array.isArray(batches) && batches.some((batch) => !getSelectedBatchId(batch));
  });
}

// Batch info (name, start date, venue) for each order item, meant to be embedded
// inside the existing course-title header card — not a standalone card. Combo
// products list one row per included course (course name shown per row); a single
// course omits the name since it is already the card title. `variant="light"`
// renders white text for the hero header. Batch fields (name / commencementDate /
// venueName) are backfilled onto selectedBatches by the order normalizer.
function CourseBatchDetails({ orderItems, variant }) {
  const { orgId } = useParams();
  const rows = (orderItems || []).flatMap((item) => {
    const meta = item?.productMeta;
    if (!meta) return [];
    if (Array.isArray(meta.comboElements) && meta.comboElements.length) {
      return meta.comboElements.map((el, i) => ({
        key: `${item?.skuId || meta?.id}-${el?.course?.id || i}`,
        courseName: el?.course?.name || 'N/A',
        stream: el?.course?.stream?.name || null,
        batch: meta?.selectedBatches?.[i],
      }));
    }
    return [{
      key: item?.skuId || meta?.id || 'course',
      courseName: meta?.course?.name || meta?.name || 'N/A',
      stream: meta?.course?.stream?.name || null,
      batch: meta?.selectedBatches?.[0],
    }];
  }).filter((row) => row.batch?.name || isBatchPending(row.batch));

  if (!rows.length) return null;

  // Streams (CE, ME, EE, …) are how Made Easy's catalogue is actually organised and
  // are what a student picked at enrolment. Elsewhere a course carries one nominal
  // stream that just restates its own name, so the field is dropped there. Every route
  // that renders this is nested under `:orgId`, so the param is always present.
  const showStream = isMadeEasyOrg(orgId);
  const light = variant === 'light';
  const labelCls = light ? 'text-white/70' : 'text-[#64748b]';
  const valueCls = light ? 'text-white font-medium' : 'text-[#334155] font-medium';
  const showCourseName = rows.length > 1;

  return (
    <div className={`mt-3 flex flex-col gap-3 w-full ${light ? 'items-center text-center' : 'items-start'}`}>
      {rows.map((row) => {
        const timing = formatTiming(row.batch);
        const pending = isBatchPending(row.batch);
        // Built as a list rather than inline JSX so the fields that drop out (timing
        // on bad data, venue on an online batch) leave no gap behind, and so every
        // field that survives can take an equal share of the width below.
        const fields = [
          ...(showStream ? [{ label: 'Stream', value: row.stream || '—' }] : []),
          { label: 'Batch Name', value: row.batch?.name },
          { label: 'Batch Start Date', value: formatDate(row.batch?.commencementDate) },
          ...(timing !== null ? [{ label: 'Batch Timing', value: timing }] : []),
          ...(hasVenue(row.batch) ? [{ label: 'Batch Venue', value: row.batch.venueName }] : []),
        ];
        return (
          <div key={row.key} className="flex flex-col gap-1 w-full">
            {showCourseName && (
              <div className={`text-[14px] font-semibold ${light ? 'text-white' : 'text-[#0f172a]'}`}>
                {row.courseName}
              </div>
            )}
            {pending && (
              <div className={`text-[13px] ${light ? 'text-amber-200' : 'text-amber-600'}`}>
                Batch not selected yet
              </div>
            )}
            {/* `flex-1` on every field spreads them evenly across the card instead of
                bunching them at the left edge; `min-w` is what makes them wrap onto a
                second line on a phone rather than crushing together. */}
            <div className={`flex flex-wrap gap-x-6 gap-y-3 w-full text-[13px] ${pending ? 'hidden' : ''}`}>
              {fields.map((field) => (
                <div key={field.label} className="flex flex-col flex-1 min-w-[110px]">
                  <span className={labelCls}>{field.label}</span>
                  <span className={valueCls}>{field.value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

CourseBatchDetails.propTypes = {
  orderItems: PropTypes.array,
  variant: PropTypes.oneOf(['card', 'light']),
};

CourseBatchDetails.defaultProps = {
  orderItems: [],
  variant: 'card',
};

export default CourseBatchDetails;
